// ── VendorRenewalChainGroup — ORA.9 vendor list-page renewal chain grouping ────
//
// Renders a parent RA entry followed by its renewal children (indented + labelled).
// Threshold: ≤3 renewals fully expanded; ≥4 collapsed behind "Show all N renewals".
//
// D-ORA-ARCH-9 / MI ORA.9 scope: PARALLEL component (vendor-domain only).
// ORA parallel: OrgRenewalChainGroup.tsx

import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  CheckCircle,
  XCircle,
  RotateCcw,
  MoreHorizontal,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { RrTypeChip } from '@/components/risk-acceptances/RrTypeChip';
import { RrStatusCell } from '@/components/risk-acceptances/RrStatusCell';
import { ApprovedCell, ExpiresCell } from '@/components/risk-acceptances/rrDates';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { RaEntry } from '@/api/types/risk-acceptance.types';

export type VendorRaActionType =
  | 'approve' | 'deny' | 'return'
  | 'close-as-mitigated' | 'revoke'
  | 'approve-renewal' | 'deny-renewal';

const COLLAPSE_THRESHOLD = 3;

// ── "Approved" column (item 2) ────────────────────────────────────────────────
// Shows the real approved_at date for rows that WERE approved — which is approved + the
// post-approval terminals (expired / revoked / closed_mitigated all passed through approved,
// so they carry approved_at). ⚠ approved_at PRESENCE is the oracle (not a hardcoded state list):
// null approved_at → a never-yet-approved state → an HONEST PER-STATE label, never a date. This
// sidesteps the updated_at worker-co-stamp artifact (expired rows had updated_at ≈ expires_at).
const UNAPPROVED_STATE_LABEL: Record<string, string> = {
  pending_approval: 'Awaiting Approval',
  returned: 'Action Required',
  draft: 'Draft',
  denied: 'Denied',
};

// ── RR-UX-1 S1: Approved/Expires now render via the SHARED rrDates module (actual dates + urgency, one
// treatment across vendor + org). The vendor keeps its per-state label for the UNAPPROVED case as the
// Approved fallback (org uses an em dash). ──
function unapprovedLabel(entry: RaEntry): ReactNode {
  return <span className="text-xs text-text-faint italic">{UNAPPROVED_STATE_LABEL[entry.workflow_status] ?? '—'}</span>;
}

interface VendorRenewalChainGroupProps {
  parent: RaEntry;
  children: RaEntry[];
  isApproverClass: boolean;
  currentUserId: string | null;
  vendorName: (vendorId: string) => string;
  onNavigate: (id: string) => void;
  onAction: (action: VendorRaActionType, entry: RaEntry) => void;
}

export function VendorRenewalChainGroup({
  parent,
  children,
  isApproverClass,
  currentUserId,
  vendorName,
  onNavigate,
  onAction,
}: VendorRenewalChainGroupProps) {
  const [expanded, setExpanded] = useState(children.length <= COLLAPSE_THRESHOLD);
  const isChain = children.length > 0;

  // ⚠ A renewal chain reads as ONE tinted block (#152641) joined by a tree connector: the Original row
  // + its indented children. ⚠ REQUIRED honesty: renewal links are DERIVED from parent → child lineage,
  // never a logged "renewed" event — the derived note states that explicitly.
  return (
    <div className={isChain ? 'bg-[#152641]/50' : ''}>
      <VendorGroupRow
        entry={parent}
        isChild={false}
        isOriginal={isChain}
        renewalIndex={undefined}
        isApproverClass={isApproverClass}
        currentUserId={currentUserId}
        vendorName={vendorName}
        onNavigate={onNavigate}
        onAction={onAction}
      />

      {isChain && (
        <div className="px-4 pb-1.5 pl-9 text-[10.5px] text-text-faint italic border-b border-border-subtle">
          ↳ Renewal links are derived from parent → child lineage — not a logged “renewed” event.
        </div>
      )}

      {children.length > COLLAPSE_THRESHOLD && (
        <div className="px-4 py-1.5 border-b border-border-subtle bg-bg-surface-2 pl-9">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs text-text-secondary gap-1"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3 w-3" />
                Collapse renewals
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" />
                Show all {children.length} renewals
              </>
            )}
          </Button>
        </div>
      )}

      {expanded && children.map((child, idx) => (
        <VendorGroupRow
          key={child.id}
          entry={child}
          isChild
          renewalIndex={idx + 1}
          isApproverClass={isApproverClass}
          currentUserId={currentUserId}
          vendorName={vendorName}
          onNavigate={onNavigate}
          onAction={onAction}
        />
      ))}
    </div>
  );
}

// ── VendorOrphanChildRow ──────────────────────────────────────────────────────
//
// Flat standalone row for a renewal child whose parent is absent from the
// current page (e.g. filtered out or on a different page).

interface VendorOrphanChildRowProps {
  entry: RaEntry;
  isApproverClass: boolean;
  currentUserId: string | null;
  vendorName: (vendorId: string) => string;
  onNavigate: (id: string) => void;
  onAction: (action: VendorRaActionType, entry: RaEntry) => void;
}

export function VendorOrphanChildRow(props: VendorOrphanChildRowProps) {
  return (
    <VendorGroupRow
      entry={props.entry}
      isChild
      renewalIndex={undefined}
      isApproverClass={props.isApproverClass}
      currentUserId={props.currentUserId}
      vendorName={props.vendorName}
      onNavigate={props.onNavigate}
      onAction={props.onAction}
    />
  );
}

// ── VendorGroupRow (internal) ─────────────────────────────────────────────────

interface VendorGroupRowProps {
  entry: RaEntry;
  isChild: boolean;
  renewalIndex: number | undefined;
  isOriginal?: boolean; // the head of a chain (has ≥1 renewal child) — gets the "Original" branch node
  isApproverClass: boolean;
  currentUserId: string | null;
  vendorName: (vendorId: string) => string;
  onNavigate: (id: string) => void;
  onAction: (action: VendorRaActionType, entry: RaEntry) => void;
}

function VendorGroupRow({
  entry,
  isChild,
  renewalIndex,
  isOriginal,
  isApproverClass,
  currentUserId,
  vendorName,
  onNavigate,
  onAction,
}: VendorGroupRowProps) {
  const showActionMenu =
    isApproverClass && (
      entry.workflow_status === 'pending_approval' ||
      entry.workflow_status === 'approved'
    );

  const isRenewalChild = entry.parent_acceptance_id !== null;

  return (
    <div
      // ⚠ RR-UX-1 S1: empty-band fix (mirrors org 2a) — Vendor/Title flex, Type+State auto (no balloon), the
      // Approved/Expires date twins are FIXED 7rem, right-anchored, so they align + the old 1fr gap is gone.
      className={`grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto_7rem_7rem_auto] gap-4 px-4 py-3 border-b border-border-subtle last:border-0 items-center hover:bg-bg-surface-2/60 transition-colors ${isChild ? 'pl-8 ml-3 border-l-2 border-accent-cyan/25' : isOriginal ? 'border-l-2 border-accent-cyan/50' : ''}`}
    >
      {/* Vendor */}
      <button
        className="text-left truncate text-sm text-text-secondary hover:text-text-primary"
        onClick={() => onNavigate(entry.id)}
      >
        {entry.vendor_id ? vendorName(entry.vendor_id) : '—'}
      </button>

      {/* Title — with "Renewal N" prefix for children */}
      <button
        className="text-left text-sm font-medium text-text-primary hover:text-accent-cyan flex items-center gap-1.5 min-w-0"
        onClick={() => onNavigate(entry.id)}
      >
        {isChild && (
          <span className="text-text-secondary text-xs font-normal shrink-0">
            ↳ {renewalIndex != null ? `Renewal ${renewalIndex}` : 'Renewal'}
          </span>
        )}
        {isOriginal && (
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide rounded border border-accent-cyan/40 bg-accent-cyan/10 text-accent-cyan px-1.5 py-0.5">Original</span>
        )}
        <span className="truncate">{entry.title ?? '—'}</span>
      </button>

      {/* Type + State — the type chip sits LEFT of the status; the pair wraps together (SS2B-2b) */}
      <div className="flex flex-wrap items-center gap-1.5">
        <RrTypeChip entry={entry} />
        <RrStatusCell entry={entry} />
      </div>

      {/* Approved — the ACTUAL date (RR-UX-1: never relative), or the per-state label when unapproved */}
      <ApprovedCell iso={entry.approved_at} fallback={unapprovedLabel(entry)} />

      {/* Expires — actual date + urgency pill; "No expiry" when null (nullable since V990) */}
      <ExpiresCell iso={entry.acceptance_expires_at} />

      {/* Action menu */}
      <div className="flex justify-end w-8">
        {showActionMenu ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {entry.workflow_status === 'pending_approval' ? (
                isRenewalChild ? (
                  <>
                    <DropdownMenuItem
                      disabled={currentUserId === entry.submitted_by_user_id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (currentUserId !== entry.submitted_by_user_id) {
                          onAction('approve-renewal', entry);
                        }
                      }}
                    >
                      <CheckCircle className="mr-2 h-3.5 w-3.5 text-accent-emerald" />
                      Approve Renewal
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); onAction('deny-renewal', entry); }}
                      className="text-accent-rose"
                    >
                      <XCircle className="mr-2 h-3.5 w-3.5" />
                      Deny Renewal
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); onAction('approve', entry); }}
                    >
                      <CheckCircle className="mr-2 h-3.5 w-3.5 text-accent-emerald" />
                      Approve
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); onAction('deny', entry); }}
                      className="text-accent-rose"
                    >
                      <XCircle className="mr-2 h-3.5 w-3.5" />
                      Deny
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); onAction('return', entry); }}
                    >
                      <RotateCcw className="mr-2 h-3.5 w-3.5" />
                      Return for Info
                    </DropdownMenuItem>
                  </>
                )
              ) : (
                <>
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); onAction('close-as-mitigated', entry); }}
                  >
                    <CheckCircle className="mr-2 h-3.5 w-3.5 text-accent-emerald" />
                    Close as Mitigated
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={currentUserId === entry.submitted_by_user_id}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentUserId !== entry.submitted_by_user_id) {
                        onAction('revoke', entry);
                      }
                    }}
                    className="text-accent-rose"
                  >
                    <XCircle className="mr-2 h-3.5 w-3.5" />
                    Revoke
                  </DropdownMenuItem>
                  {entry.renewal_window_open && (
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); onNavigate(entry.id); }}
                    >
                      <RefreshCw className="mr-2 h-3.5 w-3.5 text-accent-cyan" />
                      Request Renewal…
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
}
